import { Link } from 'react-router-dom';
import { homeSections, categories } from '@/mocks/comics';
import ComicCard from '@/components/base/ComicCard';
import { useState, useEffect, useCallback } from 'react';

function formatViews(v: number): string {
  if (v >= 10000) return (v / 10000).toFixed(1) + '万';
  return v.toString();
}

export default function Home() {
  const [currentHero, setCurrentHero] = useState(0);

  const nextHero = useCallback(() => {
    setCurrentHero(prev => (prev + 1) % homeSections.heroComics.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextHero, 5000);
    return () => clearInterval(timer);
  }, [nextHero]);

  const heroComic = homeSections.heroComics[currentHero];

  return (
    <div>
      <HeroSection comic={heroComic} current={currentHero} total={homeSections.heroComics.length} onNext={nextHero} onSelect={setCurrentHero} />
      <CategoryNav />
      <HotComicsSection />
      <LatestUpdatesSection />
      <NewComicsSection />
      <TopRatedSection />
      <CTASection />
    </div>
  );
}

function HeroSection({ comic, current, total, onNext, onSelect }: {
  comic: typeof homeSections.heroComics[0];
  current: number;
  total: number;
  onNext: () => void;
  onSelect: (i: number) => void;
}) {
  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={comic.coverImage}
          alt={comic.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
      </div>
      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-6 flex items-center">
        <div className="flex flex-col lg:flex-row items-start gap-8 w-full">
          <div className="flex-shrink-0 hidden lg:block">
            <img
              src={comic.coverImage}
              alt={comic.title}
              className="w-48 h-64 object-cover rounded-lg"
            />
          </div>
          <div className="flex flex-col justify-center max-w-2xl pt-16 lg:pt-0">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-xs bg-primary-500 text-white rounded-full whitespace-nowrap">
                {comic.status}
              </span>
              <span className="px-2 py-0.5 text-xs bg-accent-500 text-white rounded-full whitespace-nowrap">
                {comic.category}
              </span>
              <span className="text-xs text-white/70">{comic.author} 作品</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-heading text-white mb-4">
              {comic.title}
            </h2>
            <p className="text-sm md:text-base text-white/80 line-clamp-3 mb-6 leading-relaxed">
              {comic.description}
            </p>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <span className="text-white font-semibold">{comic.rating}</span>
              </div>
              <div className="flex items-center gap-1 text-white/70">
                <i className="ri-eye-line"></i>
                <span>{formatViews(comic.views)} 次阅读</span>
              </div>
              <div className="flex items-center gap-1 text-white/70">
                <i className="ri-file-list-3-line"></i>
                <span>{comic.chapters.length} 话</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to={'/article/' + comic.slug}
                className="px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to={'/category/' + comic.categorySlug}
                className="px-6 py-2.5 bg-white/20 text-white text-sm font-medium rounded-full hover:bg-white/30 transition-colors duration-200 whitespace-nowrap backdrop-blur-sm"
              >
                更多{comic.category}漫画
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {homeSections.heroComics.map((c, i) => (
          <button
            key={c.id}
            onClick={() => onSelect(i)}
            className={
              'w-2 h-2 rounded-full transition-all duration-300 ' +
              (i === current ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/70')
            }
            aria-label={'切换到第' + (i + 1) + '个推荐'}
          />
        ))}
      </div>

      <button
        onClick={onNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors duration-200 backdrop-blur-sm z-10"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="py-8 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {categories.map(cat => (
            <Link
              key={cat.slug}
              to={'/category/' + cat.slug}
              className="flex flex-col items-center gap-2 p-4 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors duration-200 group"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-200">
                <i className={cat.icon + ' text-lg'}></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotComicsSection() {
  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950">热门推荐</h2>
            <p className="text-sm text-foreground-600 mt-1">大家都在看的韩国漫画</p>
          </div>
          <Link
            to="/category/romance"
            className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 flex items-center gap-1 whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {homeSections.hotComics.map(comic => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestUpdatesSection() {
  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950">最近更新</h2>
            <p className="text-sm text-foreground-600 mt-1">最新的漫画章节已就绪</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {homeSections.latestUpdates.map(comic => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NewComicsSection() {
  if (homeSections.newComics.length === 0) return null;
  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950">新作上线</h2>
            <p className="text-sm text-foreground-600 mt-1">新鲜出炉的漫画作品</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {homeSections.newComics.map(comic => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TopRatedSection() {
  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950">高分排行</h2>
            <p className="text-sm text-foreground-600 mt-1">读者评分最高的漫画</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 min-w-[640px]">
            {homeSections.topRated.slice(0, 10).map((comic, index) => (
              <Link
                key={comic.id}
                to={'/article/' + comic.slug}
                className="flex items-center gap-3 p-3 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors duration-200 group"
              >
                <div className={
                  'w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-sm flex-shrink-0 ' +
                  (index < 3 ? 'bg-accent-500' : 'bg-secondary-400')
                }>
                  {index + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                    {comic.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-foreground-500">{comic.category}</span>
                    <span className="text-xs text-accent-500 font-medium">{comic.rating}分</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-14 md:py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-foreground-950" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,oklch(0.55_0.20_25),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,oklch(0.68_0.16_80),transparent_60%)]" />
      </div>
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-heading text-white mb-4">
          发现更多好看的韩国漫画
        </h2>
        <p className="text-white/70 mb-8 max-w-xl mx-auto">
          韩漫星球每天更新最新韩漫，免费无删减下拉式阅读，为您带来流畅愉悦的漫画阅读时光
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/romance"
            className="px-8 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            开始探索
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 bg-white/10 text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors duration-200 whitespace-nowrap"
          >
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}